import { Trend, TrendOptions } from 'core-ui/SelectTrendInput/SelectTrendInput';

export const WidgetAction = {
  SORT: 'SORT',
  SWITCH_BAR_LINE: 'SWITCH_BAR_LINE',
  SWITCH_CHART_TABLE: 'SWITCH_CHART_TABLE',
  SWITCH_LINECHART_TABLE: 'SWITCH_LINECHART_TABLE',
  NONE: 'NONE',
  METRICS_DROPDOWN: 'METRICS_DROPDOWN',
  SEARCH_FILTER: 'SEARCH_FILTER',
  SHOW_LINE_CHART: 'SHOW_LINE_CHART',
  EXPORT_TO_CSV: 'EXPORT_TO_CSV',
  EXPORT_TO_XLSX: 'EXPORT_TO_XLSX',
  EXPANDABLE: 'EXPANDABLE',
  EXPORT_TO_HTML: 'EXPORT_TO_HTML',
  EXPORT_TO_PNG: 'EXPORT_TO_PNG',
  EXPORT_TO_JPEG: 'EXPORT_TO_JPEG',
  // Add here every future actions.
  // eslint-disable-next-line prettier/prettier
} as const;
export type WidgetAction = (typeof WidgetAction)[keyof typeof WidgetAction];

export interface BaseWidget {
  width?: number;
  height?: number;
  loading?: boolean;
  displayName?: string;
  context?: any;
  response: any;
  [x: string]: any;
}
interface TableOptions {
  striped?: boolean;
  pagination?: boolean;
  paginationServer?: boolean;
  sortServer?: boolean;
  fixedHeader?: boolean;
  showTotals?: boolean;
}

export const TableType = {
  NONE: 'NONE',
  NORMAL: 'NORMAL',
  ENTERPRISE: 'ENTERPRISE',
} as const;

interface ChartOptions {
  colors?: string[];
}

interface WidgetConfig {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  static: boolean;
  isDraggable?: boolean;
  moved?: boolean;
  maxH?: number;
  maxW?: number;
  minH?: number;
  minW?: number;
}

interface TextBlockStyle {
  color: string;
  textAlign: 'left' | 'right' | 'center';
  fontSize: number;
}

export interface TextBlockInterface {
  textContent: string;
  styles: TextBlockStyle;
}

interface CardOptions {
  options: {
    showTitle: boolean;
    showSubtitle: boolean;
    showActions: boolean;
    showCard: boolean;
    actions: string[];
    textBlock?: TextBlockInterface;
    defaults?: any;
    table?: TableOptions;
    decimalPlaces?: number;
  };
}

export interface TWidgetsConfig {
  w: number;
  h: number;
  minW: number;
  minH: number;
}

export interface WidgetCollectionAttributes extends CardOptions {
  config: TWidgetsConfig;
  image: string;
  actions: string[];
  colors: string[];
}

export interface WidgetAlias {
  controllerName: string;
  attributes: WidgetCollectionAttributes;
}

export interface WidgetCollection {
  component: string;
  description: string;
  defaults: WidgetCollectionAttributes;
  alias: WidgetAlias[];
  dataSourceTypes: string[];
}

/**
 * Filter types used in CustomConfig.filters
 */
export interface FilterDateOption {
  displayName: string;
  type: string;
  startDate: string;
  endDate: string;
  isEditable: boolean;
}

export interface HierarchyLevel {
  backEndTypeAhead: boolean;
  label: string;
  attributeName: string;
  colPosition: number;
  updateUrl: string;
  typeAheadUrl: string | null;
  choices: Array<{
    label: string;
    value: string;
    sortOrder: number;
    preSelected: boolean;
  }>;
}

export interface HierarchyFilterOption {
  levels: HierarchyLevel[];
}

export interface AdvancedFilterOption {
  displayName: string;
  attributeName: string;
  attributeGuid: string;
  opType: string;
  operatorOption: Record<string, any>;
  subAttribute: string | null;
  values: string[];
}

/**
 * Response option for a field returned by the GuardRails API inside Filters.AdvancedFilters.
 */
export interface AdvancedFilterResponseOption {
  id: number | null;
  cxChoiceGuid: string;
  choice: string;
  flag: string | null;
  rating: number | null;
}

/**
 * Represents a field available for advanced filtering, as returned by the
 * GuardRails API inside Config.Filters.AdvancedFilters when entering a partition.
 */
export interface AdvancedFilterFieldConfig {
  displayName: string;
  columnName: string;
  attributeName: string;
  attributeGuid: string;
  opType: string | null;
  operatorOptions: Record<string, string>;
  subAttribute: string | null;
  longText: string;
  shortText: string;
  typeId: number;
  typeName: string;
  values: string[];
  responses: AdvancedFilterResponseOption[];
}

export interface FiltersConfig {
  dateFilter?: FilterDateOption[];
  hierarchyFilters?: HierarchyFilterOption[];
  advancedFilters?: AdvancedFilterFieldConfig[];
  currentSelection?: {
    dateFilter?: FilterDateOption | null;
    hierarchyFilters?: Array<{
      attributeName: string;
      displayName: string;
      values: string[];
      colPosition: number;
    }> | null;
    advancedFilters?: AdvancedFilterOption[] | null;
  } | null;
}

/**
 * Question model in the new camelCase shape
 */
export interface QuestionModel {
  questionLabel: string;
  qName: string;
  displayFormat: string;
  dataType: string;
  shortText: string;
  longText: string;
  isUnscaledPercent: boolean;
  isOtherSpecify: number;
  iteration: number;
  isOS: boolean;
  numericRangeMin: number;
  numericRangeMax: number;
  publicKey: string;
}

/**
 * Calculation model in the new camelCase shape
 */
export interface CalculationModel {
  id: number;
  calculationLabel: string;
  calculationFormula: string;
  displayFormat: string;
  dataType: string;
  isUnscaledPercent: boolean;
  coreCalcTag: string;
  decimalFormat: number;
  questionName: string;
  displayName: string;
}

/**
 * Custom calculation model in the new camelCase shape
 */
export interface CustomCalculationModel {
  type: string;
  displayName: string;
  decimalPrecision: number;
  statTesting: boolean;
  normalize: boolean;
  /** Response values eligible for normalization - excludes flagged responses (DK/NA/REF) */
  normalizeValues: (number | string | boolean)[];
  metadata: Record<string, any>;
}

/**
 * Rule model in the new camelCase shape
 */
export interface RuleModel {
  friendlyName: string;
  description: string;
  hasMedusaQuery: boolean;
  ruleName: string;
  questions: { min: number; max: number };
  calculations: { min: number; max: number };
}

/**
 * Trend option in the new camelCase shape
 */
export interface TrendOptionModel {
  displayName: string;
  timePeriodType: string;
  intervalMaxValue: number;
  intervalMinValue: number;
  selectedValue: number;
}

/**
 * Field response model in the new camelCase shape
 */
export interface FieldResponseModel {
  id: number;
  cxChoiceGuid: string;
  choice: string;
  flag: string | null;
  rating: number;
}

/**
 * Field model in the new camelCase shape
 */
export interface FieldModel {
  columnName: string;
  typeId: number;
  typeName: string;
  fieldGuid: string;
  longText: string;
  shortText: string;
  questionVariable: string;
  uniqueName: string;
  responses: FieldResponseModel[];
}

/**
 * Query collection model for Medusa queries
 */
export interface QueryCollectionModel {
  queryId: string;
  queryName: string;
  createdDate: string;
}

/**
 * Enterprise partition collection model with fields and trend data
 */
export interface EnterprisePartitionModel {
  dataCollectionGuid: string | null;
  originId: string | null;
  trend: any;
  fields: any[];
}

/**
 * Data collection model in the new camelCase shape
 */
export interface DataCollectionModel {
  originId: string;
  dataCollectionGuid: string;
  dataCollectionName: string;
}

/**
 * Comparison option model in the new camelCase shape
 */
export interface ComparisonOptionModel {
  choices: Array<{
    firstPeriod: string;
    secondPeriod: string;
    displayName: string;
    description: string;
  }>;
  currentSelection: {
    firstPeriod: string;
    secondPeriod: string;
  };
}

export interface CustomConfig {
  chart?: string;
  trend?: TrendOptionModel[] | null;
  questions?: QuestionModel[];
  calculations?: CalculationModel[];
  customCalculations?: CustomCalculationModel[];
  rules?: RuleModel[];
  metadata?: Record<string, any>;
  dataSource?: string;
  excludeTrendFor?: string[];
  filters?: FiltersConfig | null;
  decimalFormat?: number;
  comparisonOptions?: ComparisonOptionModel | null;
  dataCollectionsList?: DataCollectionModel[];
  queryCollections?: (QueryCollectionModel | EnterprisePartitionModel)[];
  fields?: FieldModel[];
}

export interface Filters {
  currentSelection?: CurrentSelection | null | undefined;
}

export interface CurrentSelection {
  dateFilter?: DateFilter | null;
  hierarchyFilters?: HierarchyFilter[] | null;
  advancedFilters?: AdvancedFilter[] | null;
}

export interface DateFilter {
  displayName: string;
  endDate?: string;
  isEditable: boolean;
  startDate?: string;
  type: string;
}

export interface HierarchyFilter {
  attributeName: string;
  values: string[];
}

export interface AdvancedFilter {
  attributeName: string;
  values: string[];
}

export interface Widget extends TableOptions, ChartOptions, CardOptions {
  id?: string;
  alias: string;
  controllerName?: string;
  description: string;
  dataSourceTypes: string[];
  displayName: string;
  config: WidgetConfig;
  payload: any;
  actions?: string[];
  subText?: string;
  image: string;
  viewName: string;
  isCustom?: boolean;
  isMockData?: boolean;
  hasMedusaQuery?: boolean;
  viewId: string;
  colors: string[];
  url?: string;
  customWidgetConfig?: CustomConfig;
}

export const initWidget = (
  wCollection: WidgetCollection,
  alias: WidgetAlias,
  WidgetConfig: any
) => {
  // Combine the default attributes with the alias attributes.
  const config = {
    ...wCollection?.defaults?.config,
    ...alias?.attributes?.config,
  } as unknown as WidgetConfig;

  const image = alias?.attributes?.image ? alias.attributes.image : wCollection.defaults?.image;
  const actions = alias?.attributes?.actions.length
    ? alias?.attributes.actions
    : wCollection.defaults?.actions;
  const options = { ...wCollection.defaults?.options, ...alias?.attributes?.options, actions };
  const colors =
    WidgetConfig.colors || alias?.attributes.colors
      ? WidgetConfig.colors || alias?.attributes.colors
      : wCollection?.defaults?.colors;

  let viewId = '';
  if (WidgetConfig.viewId) {
    viewId = WidgetConfig.viewId;
  } else if (WidgetConfig.payload?.viewId) {
    viewId = WidgetConfig.payload?.viewId.toString();
  } else if (WidgetConfig.payload?.ViewId) {
    viewId = WidgetConfig.payload?.ViewId.toString();
  }

  const widget: Widget = {
    alias: wCollection.component,
    controllerName: WidgetConfig.controllerName,
    displayName: WidgetConfig.displayName,
    description: wCollection.description,
    dataSourceTypes: wCollection.dataSourceTypes,
    config,
    image,
    options,
    actions,
    ...(WidgetConfig.url ? { url: WidgetConfig.url } : {}),
    payload: WidgetConfig?.payload,
    colors: colors,
    viewName: WidgetConfig.viewName,
    viewId: viewId,
    isCustom: false,
  };

  return widget;
};

export const ChartMapping = {
  0: 'Unknown',
  1: 'PieChart',
  2: 'HorizontalBarChart',
  3: 'VerticalBarChart',
  4: 'HorizontalSingleStackedBarChart',
  5: 'BulletChart',
  6: 'DataGrid',
  7: 'GeoMap',
  8: 'StackedBarChart',
  10: 'IndividualMetric',
  12: 'TopFive',
  13: 'TopCategories',
  14: 'BulletChart',
  15: 'BiggestMovers',
  16: 'MultipleSummaryMetrics',
};
